<template>
    <div class="main_bd voiceai-page">
        <div class="head_title">Voice AI Stores</div>
        <div class="page-head">
            <div class="page-sub">Add a store by SID, set its phone numbers, toggle Voice AI, and click Edit to manage hours &amp; promotions.</div>
            <div class="cluster-wrap">
                <span class="cluster-lbl">Cluster</span>
                <a-select v-model="cluster" style="width: 170px;" @change="onClusterChange">
                    <a-select-option value="north_america">North America</a-select-option>
                    <a-select-option value="asia">Southeast Asia</a-select-option>
                </a-select>
            </div>
            <div class="stat-wrap">
                <div class="stat-item">
                    <div class="stat-n">{{ total_count }}</div>
                    <div class="stat-l">Stores</div>
                </div>
                <div class="stat-item">
                    <div class="stat-n on">{{ voiceai_enabled_count }}</div>
                    <div class="stat-l">Voice AI On</div>
                </div>
            </div>
        </div>

        <a-card class="add-card" :bordered="true">
            <div class="add-lbl">Add a store by SID (merchant_id)</div>
            <div class="add-row">
                <a-input
                    v-model="add_sid"
                    placeholder="e.g. s8435ffb889dcd2015a9325ce2f33c564"
                    style="flex: 1;"
                    @pressEnter="doAddStore"
                />
                <a-button type="primary" :loading="adding" @click="doAddStore">+ Add store</a-button>
            </div>
            <div class="add-hint">On Add, store name and timezone are pulled automatically from storeInfo. Then set numbers and click Edit for hours &amp; promotions.</div>
        </a-card>

        <a-table
            :loading="loading"
            :pagination="false"
            :rowKey="row => row.sid"
            :columns="columns"
            :data-source="list"
        >
            <template slot="store" slot-scope="text, row">
                <div class="store-name">{{ row.sname || '—' }}</div>
                <div class="store-sid">{{ row.sid }}</div>
            </template>
            <template slot="voice" slot-scope="text, row">
                <a-switch
                    :checked="row.voiceai_enabled == 1"
                    @change="onToggleEnable(row, $event)"
                />
                <span class="voice-label" :class="{ on: row.voiceai_enabled == 1 }">
                    {{ row.voiceai_enabled == 1 ? 'On' : 'Off' }}
                </span>
            </template>
            <template slot="inbound" slot-scope="text, row">
                <span v-if="row.inbound_number" class="phone-val">
                    {{ row.inbound_number }}
                    <a class="phone-edit" @click="openPhoneModal(row, 'inbound_number')">✎</a>
                </span>
                <a-button v-else size="small" @click="openPhoneModal(row, 'inbound_number')">+ Add</a-button>
            </template>
            <template slot="merchant_phone" slot-scope="text, row">
                <span v-if="row.merchant_number" class="phone-val">
                    {{ row.merchant_number }}
                    <a class="phone-edit" @click="openPhoneModal(row, 'merchant_number')">✎</a>
                </span>
                <a-button v-else size="small" @click="openPhoneModal(row, 'merchant_number')">+ Add</a-button>
            </template>
            <template slot="source" slot-scope="text, row">
                <a-select
                    style="width: 130px;"
                    :value="row.source || undefined"
                    placeholder="Select"
                    allowClear
                    @change="onSourceChange(row, $event)"
                >
                    <a-select-option v-for="s in source_options" :key="s" :value="s">{{ s }}</a-select-option>
                </a-select>
            </template>
            <template slot="action" slot-scope="text, row">
                <div class="action-btns">
                    <a-button size="small" @click="openDrawer(row)">Edit</a-button>
                    <a-button size="small" style="margin-left: 8px;" :loading="row._syncing" @click="doSync(row)">Sync</a-button>
                    <a-button size="small" type="danger" style="margin-left: 8px;" @click="doDelete(row)">Delete</a-button>
                </div>
            </template>
        </a-table>

        <a-pagination
            style="margin-top: 12px;"
            :pageSize="20"
            v-model="cur_page"
            :total="total_count"
            show-less-items
            @change="turnPage"
        />

        <div class="foot-note">
            This screen manages Voice AI store settings used by the inbound webhook.
            Edit opens this store's hours, special dates, promotions and call-ahead settings.
        </div>

        <a-modal
            :title="phone_modal_title"
            :visible="phone_modal_visible"
            :confirmLoading="phone_saving"
            okText="Save"
            @ok="savePhone"
            @cancel="closePhoneModal"
        >
            <div style="margin-bottom: 8px;">Phone number</div>
            <div class="phone-input-row">
                <div v-if="cluster !== 'asia'" class="phone-cc">🇺🇸 +1</div>
                <a-input
                    v-model="phone_digits"
                    :placeholder="cluster === 'asia' ? '+65 91234567' : '8881112211'"
                    :maxLength="cluster === 'asia' ? 16 : 10"
                    @input="onPhoneInput"
                    @pressEnter="savePhone"
                />
            </div>
            <div class="add-hint">
                {{ cluster === 'asia'
                    ? 'Enter phone with country code, e.g. +65 91234567 (max 16 chars).'
                    : 'Enter the 10-digit number only. Saved as +1 XXXXXXXXXX.' }}
            </div>
            <div v-if="phone_error" class="phone-error">{{ phone_error }}</div>
        </a-modal>

        <a-drawer
            title="Voice AI Store Info"
            :width="720"
            placement="right"
            :visible="drawer_visible"
            @close="closeDrawer"
        >
            <div v-if="edit_row" class="drawer-meta">
                <div class="drawer-store">{{ edit_row.sname || '—' }}</div>
                <div class="drawer-sid">{{ edit_row.sid }}</div>
            </div>

            <div class="sect">
                <div class="sect-title">Opening Hours <span class="sect-sub">spoken to callers</span></div>
                <div v-for="(h, i) in edit_hours" :key="'h'+i" class="hour-row" :class="{ closed: !h.open }">
                    <div class="day">{{ day_shorts[i] }}</div>
                    <div class="times">
                        <a-select
                            v-model="h.from"
                            style="width: 120px;"
                            :disabled="!h.open"
                        >
                            <a-select-option v-for="opt in time_options" :key="'hf'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                        </a-select>
                        <span class="to-lbl">to</span>
                        <a-select
                            v-model="h.to"
                            style="width: 120px;"
                            :disabled="!h.open"
                        >
                            <a-select-option v-for="opt in time_options" :key="'ht'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                        </a-select>
                        <span v-if="!h.open" class="closed-lbl">Closed</span>
                    </div>
                    <a-switch v-model="h.open" size="small" />
                </div>

                <div class="sect nested">
                    <div class="sect-title warn">Special Dates <span class="sect-sub">holidays / exceptions</span></div>
                    <div v-for="(s, i) in edit_special" :key="'sp'+i" class="special-card">
                        <div class="special-row1">
                            <a-date-picker
                                :value="s.date ? moment(s.date) : null"
                                format="YYYY-MM-DD"
                                @change="(d, ds) => onSpecialDateChange(i, ds)"
                            />
                            <a-input v-model="s.label" placeholder="Label" style="flex: 1;" />
                            <a-button type="link" icon="close" @click="removeSpecial(i)" />
                        </div>
                        <div class="special-row2">
                            <a-radio-group v-model="s.mode" button-style="solid" size="small">
                                <a-radio-button value="closed">Closed all day</a-radio-button>
                                <a-radio-button value="hours">Open, these hours</a-radio-button>
                            </a-radio-group>
                            <div v-if="s.mode === 'hours'" class="special-times">
                                <span class="open-lbl">Open</span>
                                <a-select v-model="s.from" style="width: 110px;">
                                    <a-select-option v-for="opt in time_options" :key="'sf'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                </a-select>
                                <span class="to-lbl">to</span>
                                <a-select v-model="s.to" style="width: 110px;">
                                    <a-select-option v-for="opt in time_options" :key="'st'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                </a-select>
                            </div>
                        </div>
                    </div>
                    <a-button type="dashed" icon="plus" @click="addSpecial">Add special date</a-button>
                </div>

                <a-alert class="preview-box" type="info" :message="'Voice AI preview'" :description="hours_preview" show-icon />
            </div>

            <div class="sect">
                <div class="sect-title">Service Settings</div>
                <div class="svc-row">
                    <div>
                        <div class="svc-name">Allow call-ahead</div>
                        <div class="svc-desc">When off, the AI won't offer call-ahead for this store.</div>
                    </div>
                    <a-switch v-model="edit_callahead" />
                </div>
            </div>

            <div class="sect">
                <div class="sect-title">Voice AI Working Hours <span class="sect-sub">when the AI answers</span></div>
                <div class="svc-row" style="margin-bottom: 12px;">
                    <div>
                        <div class="svc-name">Limit AI to set hours</div>
                        <div class="svc-desc">When on, the AI only answers during the hours below. Outside them, calls go straight to the store. When off, the AI answers whenever Voice AI is on.</div>
                    </div>
                    <a-switch v-model="edit_ai_hours_enable" />
                </div>

                <template v-if="edit_ai_hours_enable">
                    <div v-for="(h, i) in edit_ai_hours" :key="'ah'+i" class="ai-hour-row">
                        <div class="day">{{ day_shorts[i] }}</div>
                        <div class="ai-hour-times">
                            <template v-if="h.mode === 'hours'">
                                <a-select v-model="h.from" style="width: 110px;">
                                    <a-select-option v-for="opt in time_options" :key="'ahf'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                </a-select>
                                <span class="to-lbl">to</span>
                                <a-select v-model="h.to" style="width: 110px;">
                                    <a-select-option v-for="opt in time_options" :key="'aht'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                </a-select>
                            </template>
                        </div>
                        <a-radio-group v-model="h.mode" button-style="solid" size="small">
                            <a-radio-button value="all">All day</a-radio-button>
                            <a-radio-button value="hours">Set hours</a-radio-button>
                            <a-radio-button value="off">Off</a-radio-button>
                        </a-radio-group>
                    </div>

                    <div class="sect nested">
                        <div class="sect-title warn">AI Special Dates <span class="sect-sub">holidays / exceptions</span></div>
                        <div v-for="(s, i) in edit_ai_special" :key="'asp'+i" class="special-card">
                            <div class="special-row1">
                                <a-date-picker
                                    :value="s.date ? moment(s.date) : null"
                                    format="YYYY-MM-DD"
                                    @change="(d, ds) => onAiSpecialDateChange(i, ds)"
                                />
                                <a-input v-model="s.label" placeholder="Label" style="flex: 1;" />
                                <a-button type="link" icon="close" @click="removeAiSpecial(i)" />
                            </div>
                            <div class="special-row2">
                                <a-radio-group v-model="s.mode" button-style="solid" size="small">
                                    <a-radio-button value="closed">AI off all day</a-radio-button>
                                    <a-radio-button value="hours">AI works these hours</a-radio-button>
                                </a-radio-group>
                                <div v-if="s.mode === 'hours'" class="special-times">
                                    <span class="open-lbl">AI on</span>
                                    <a-select v-model="s.from" style="width: 110px;">
                                        <a-select-option v-for="opt in time_options" :key="'asf'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                    </a-select>
                                    <span class="to-lbl">to</span>
                                    <a-select v-model="s.to" style="width: 110px;">
                                        <a-select-option v-for="opt in time_options" :key="'ast'+i+opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
                                    </a-select>
                                </div>
                            </div>
                        </div>
                        <a-button type="dashed" icon="plus" @click="addAiSpecial">Add special date</a-button>
                    </div>
                </template>
            </div>

            <div class="sect">
                <div class="sect-title">Promotions <span class="sect-sub">English only · AI translates</span></div>
                <div v-if="edit_promos.length === 0" class="empty-promo">No promotions. Add one, or leave empty.</div>
                <div v-for="(p, i) in edit_promos" :key="'p'+i" class="promo-item">
                    <a-textarea v-model="edit_promos[i]" :rows="2" />
                    <a-button type="link" icon="delete" @click="removePromo(i)" />
                </div>
                <a-button type="dashed" icon="plus" @click="addPromo">Add promotion</a-button>
                <div class="add-hint">Write once in English. The Voice AI translates into the caller's language when speaking.</div>
            </div>

            <div class="sect">
                <div class="sect-title">Online Ordering <span class="sect-sub">AI texts this link on request</span></div>
                <div class="svc-row" style="margin-bottom: 14px;">
                    <div>
                        <div class="svc-name">Enable online ordering by text</div>
                        <div class="svc-desc">When on and a caller wants to order, the AI texts them the link below. When off, the AI says online ordering isn't available.</div>
                    </div>
                    <a-switch v-model="edit_online_order_enabled" />
                </div>
                <div class="field-lbl">Online order link</div>
                <a-input
                    v-model="edit_online_order_url"
                    placeholder="https://order.examplepos.com/store/abc123"
                />
                <div class="add-hint">This store's own ordering page (from your POS / ordering system). One link per store.</div>
                <div class="field-lbl" style="margin-top: 14px;">Text message content</div>
                <a-textarea v-model="edit_order_sms_template" :rows="3" />
                <div class="add-hint">Use {link} where the link goes — filled in automatically. Keep it short.</div>
                <a-alert
                    class="preview-box"
                    type="info"
                    message="Text preview"
                    :description="order_sms_preview"
                    show-icon
                />
                <div class="add-hint">Leave the link empty or turn the switch off, and this store won't offer text-to-order.</div>
            </div>

            <div class="drawer-footer">
                <a-button style="margin-right: 8px;" @click="closeDrawer">Close</a-button>
                <a-button type="primary" :loading="drawer_saving" @click="saveDrawer">Save</a-button>
            </div>
        </a-drawer>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import moment from 'moment';

const SOURCE_OPTIONS = ['pbx', 'cmc_telecom', 'xy', 'minitable'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORTS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DEFAULT_ORDER_SMS_TEMPLATE = 'Order online here: {link}\nReply STOP to opt out.';

function buildTimeOptions() {
    const arr = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 15, 30, 45]) {
            const ap = h < 12 ? 'AM' : 'PM';
            let hh = h % 12;
            if (hh === 0) hh = 12;
            const label = `${hh}:${String(m).padStart(2, '0')} ${ap}`;
            arr.push({ label, value: label });
        }
    }
    return arr;
}

function to12h(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return '11:00 AM';
    if (/AM|PM/i.test(hhmm)) return hhmm;
    const parts = hhmm.split(':');
    let h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    if (isNaN(h)) return '11:00 AM';
    const ap = h >= 12 ? 'PM' : 'AM';
    let hh = h % 12;
    if (hh === 0) hh = 12;
    return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
}

function to24h(label) {
    if (!label) return '11:00';
    if (!/AM|PM/i.test(label)) return label;
    const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return '11:00';
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ap = match[3].toUpperCase();
    if (ap === 'AM') {
        if (h === 12) h = 0;
    } else if (h !== 12) {
        h += 12;
    }
    return `${String(h).padStart(2, '0')}:${m}`;
}

function formatUSPhone(tenDigits) {
    const d = String(tenDigits || '').replace(/\D/g, '');
    if (d.length !== 10) return null;
    return `+1 ${d}`;
}

function formatAsiaPhone(val) {
    const raw = String(val || '').trim();
    if (!raw) return null;
    // keep + and digits/spaces, max 16 for DB varchar(16)
    const cleaned = raw.replace(/[^\d+\s]/g, '').replace(/\s+/g, ' ').trim();
    if (!cleaned || cleaned.length > 16) return null;
    if (!/^\+\d[\d\s]{5,14}$/.test(cleaned)) return null;
    return cleaned;
}

function digitsOnly(val) {
    let d = String(val || '').replace(/\D/g, '');
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    return d.slice(0, 10);
}

function defaultHours() {
    return DAYS.map(() => ({ open: true, from: '11:00 AM', to: '10:00 PM' }));
}

function parseOpenningConfig(config) {
    const hours = defaultHours();
    const special = [];
    const list = Array.isArray(config) ? config : [];

    list.forEach(item => {
        if (!item) return;
        if (item.is_day == 1) {
            special.push({
                date: item.date || '',
                label: item.label || '',
                mode: item.enabled == 0 ? 'closed' : 'hours',
                from: to12h(item.start_time || '11:00'),
                to: to12h(item.end_time || '22:00')
            });
            return;
        }

        const idx = DAYS.findIndex(d => d.toLowerCase() === String(item.date || '').toLowerCase());
        if (idx >= 0) {
            hours[idx] = {
                open: item.enabled != 0,
                from: to12h(item.start_time || '11:00'),
                to: to12h(item.end_time || '22:00')
            };
        }
    });

    return { hours, special };
}

function buildOpenningConfig(hours, special) {
    const weekly = hours.map((h, i) => ({
        date: DAYS[i],
        start_time: to24h(h.from),
        end_time: to24h(h.to),
        is_day: 0,
        enabled: h.open ? 1 : 0
    }));

    const specials = (special || []).filter(s => s.date).map(s => ({
        date: s.date,
        start_time: to24h(s.from || '11:00 AM'),
        end_time: to24h(s.to || '10:00 PM'),
        is_day: 1,
        enabled: s.mode === 'closed' ? 0 : 1,
        label: s.label || ''
    }));

    return [...weekly, ...specials];
}

function defaultAiHours() {
    return DAYS.map((_, i) => (
        i < 5
            ? { mode: 'hours', from: '11:00 AM', to: '9:00 PM' }
            : { mode: 'all', from: '11:00 AM', to: '10:00 PM' }
    ));
}

function parseAiWorkingConfig(config) {
    const hours = defaultAiHours();
    const special = [];
    const list = Array.isArray(config) ? config : [];

    list.forEach(item => {
        if (!item) return;
        if (item.is_day == 1) {
            let mode = item.mode;
            if (!mode) {
                mode = item.enabled == 0 ? 'closed' : 'hours';
            }
            special.push({
                date: item.date || '',
                label: item.label || '',
                mode,
                from: to12h(item.start_time || '11:00'),
                to: to12h(item.end_time || '21:00')
            });
            return;
        }

        const idx = DAYS.findIndex(d => d.toLowerCase() === String(item.date || '').toLowerCase());
        if (idx >= 0) {
            let mode = item.mode;
            if (!mode) {
                if (item.enabled == 0) mode = 'off';
                else mode = 'hours';
            }
            hours[idx] = {
                mode,
                from: to12h(item.start_time || '11:00'),
                to: to12h(item.end_time || '21:00')
            };
        }
    });

    return { hours, special };
}

function buildAiWorkingConfig(hours, special) {
    const weekly = hours.map((h, i) => {
        const mode = h.mode || 'all';
        return {
            date: DAYS[i],
            start_time: to24h(h.from || '11:00 AM'),
            end_time: to24h(h.to || '9:00 PM'),
            is_day: 0,
            enabled: mode === 'off' ? 0 : 1,
            mode
        };
    });

    const specials = (special || []).filter(s => s.date).map(s => {
        const mode = s.mode || 'closed';
        return {
            date: s.date,
            start_time: to24h(s.from || '11:00 AM'),
            end_time: to24h(s.to || '9:00 PM'),
            is_day: 1,
            enabled: mode === 'closed' ? 0 : 1,
            mode,
            label: s.label || ''
        };
    });

    return [...weekly, ...specials];
}

export default {
    name: 'voiceai_setting',
    data() {
        return {
            loading: false,
            adding: false,
            cluster: 'north_america',
            list: [],
            total_count: 0,
            voiceai_enabled_count: 0,
            cur_page: 1,
            add_sid: '',
            source_options: SOURCE_OPTIONS,
            day_shorts: DAY_SHORTS,
            time_options: buildTimeOptions(),
            columns: [
                { title: 'Store', key: 'store', scopedSlots: { customRender: 'store' } },
                { title: 'Voice AI', key: 'voice', scopedSlots: { customRender: 'voice' }, width: 110 },
                { title: 'Agent number', key: 'inbound', scopedSlots: { customRender: 'inbound' }, width: 160 },
                { title: 'Store number', key: 'merchant_phone', scopedSlots: { customRender: 'merchant_phone' }, width: 160 },
                { title: 'Source', key: 'source', scopedSlots: { customRender: 'source' }, width: 150 },
                { title: 'Timezone', dataIndex: 'timezone', key: 'timezone', width: 170 },
                { title: '', key: 'action', scopedSlots: { customRender: 'action' }, width: 230 }
            ],
            phone_modal_visible: false,
            phone_saving: false,
            phone_field: '',
            phone_row: null,
            phone_digits: '',
            phone_error: '',
            drawer_visible: false,
            drawer_saving: false,
            edit_row: null,
            edit_hours: defaultHours(),
            edit_special: [],
            edit_promos: [],
            edit_callahead: true,
            edit_ai_hours_enable: false,
            edit_ai_hours: defaultAiHours(),
            edit_ai_special: [],
            edit_online_order_enabled: false,
            edit_online_order_url: '',
            edit_order_sms_template: DEFAULT_ORDER_SMS_TEMPLATE
        };
    },
    computed: {
        phone_modal_title() {
            return this.phone_field === 'inbound_number' ? 'Set agent number' : 'Set store number';
        },
        order_sms_preview() {
            const link = String(this.edit_online_order_url || '').trim() || 'https://order.examplepos.com/store/abc123';
            const tpl = String(this.edit_order_sms_template || '').trim() || DEFAULT_ORDER_SMS_TEMPLATE;
            return tpl.split('{link}').join(link);
        },
        hours_preview() {
            const h = this.edit_hours;
            if (!h || !h.length) return '—';
            let base;
            if (h.every(d => d.open && d.from === h[0].from && d.to === h[0].to)) {
                base = `Mon to Sun, ${h[0].from} – ${h[0].to}`;
            } else {
                base = DAY_SHORTS.map((n, i) => (h[i].open ? `${n} ${h[i].from}-${h[i].to}` : `${n} Closed`)).join('; ');
            }
            const sp = (this.edit_special || []).map(s => {
                const lbl = s.label ? ` (${s.label})` : '';
                if (s.mode === 'closed') return `${s.date || '?'}${lbl}: Closed`;
                return `${s.date || '?'}${lbl}: Open ${s.from || '?'}–${s.to || '?'}`;
            });
            return sp.length ? `${base}. ${sp.join('. ')}.` : `${base}.`;
        }
    },
    created() {
        this.fetchList();
    },
    methods: {
        moment,
        ...mapActions({
            getVoiceaiList: 'merchant/getVoiceaiList',
            createVoiceaiMerchant: 'merchant/createVoiceaiMerchant',
            updateVoiceaiEnable: 'merchant/updateVoiceaiEnable',
            updateVoiceaiSetting: 'merchant/updateVoiceaiSetting',
            updateVoiceaiContact: 'merchant/updateVoiceaiContact',
            deleteVoiceaiMerchant: 'merchant/deleteVoiceaiMerchant',
            syncVoiceaiStoreInfo: 'merchant/syncVoiceaiStoreInfo'
        }),
        onClusterChange(value) {
            this.cluster = value;
            this.cur_page = 1;
            this.fetchList();
        },
        async fetchList() {
            this.loading = true;
            try {
                const res = await this.getVoiceaiList({ page: this.cur_page, cluster: this.cluster });
                if (!res || res.code !== 100000) {
                    message.error((res && res.msg) || 'Failed to load list');
                    return;
                }
                this.list = (res.data && res.data.list) || [];
                this.total_count = (res.data && res.data.total_count) || 0;
                this.voiceai_enabled_count = (res.data && res.data.voiceai_enabled_count) || 0;
            } catch (e) {
                message.error((e && e.message) || 'Failed to load list');
            } finally {
                this.loading = false;
            }
        },
        turnPage(page) {
            this.cur_page = page;
            this.fetchList();
        },
        async doAddStore() {
            const sid = (this.add_sid || '').trim();
            if (!sid) {
                message.warning('Please input SID');
                return;
            }
            this.adding = true;
            try {
                const res = await this.createVoiceaiMerchant({ sid, source: 'pbx', cluster: this.cluster });
                if (res.code !== 100000) {
                    message.error(res.msg || 'Add failed');
                    return;
                }
                message.success('Store added');
                this.add_sid = '';
                this.cur_page = 1;
                this.fetchList();
            } catch (e) {
                message.error('Add failed');
            } finally {
                this.adding = false;
            }
        },
        async onToggleEnable(row, checked) {
            const enable = checked ? 1 : 0;
            const prev = row.voiceai_enabled;
            row.voiceai_enabled = enable;
            try {
                const res = await this.updateVoiceaiEnable({ sid: row.sid, enable, cluster: this.cluster });
                if (res.code !== 100000) {
                    row.voiceai_enabled = prev;
                    message.error(res.msg || 'Update failed');
                    return;
                }
                message.success(enable == 1 ? 'Voice AI enabled' : 'Voice AI disabled');
                this.fetchList();
            } catch (e) {
                row.voiceai_enabled = prev;
                message.error('Update failed');
            }
        },
        async onSourceChange(row, source) {
            const prev = row.source;
            const next = source === undefined || source === null ? '' : source;
            row.source = next;
            try {
                const res = await this.updateVoiceaiContact({ sid: row.sid, source: next, cluster: this.cluster });
                if (!res || res.code !== 100000) {
                    row.source = prev;
                    message.error((res && res.msg) || 'Update source failed');
                    return;
                }
                message.success('Source updated');
            } catch (e) {
                row.source = prev;
                message.error((e && e.message) || 'Update source failed');
            }
        },
        openPhoneModal(row, field) {
            this.phone_row = row;
            this.phone_field = field;
            if (this.cluster === 'asia') {
                this.phone_digits = row[field] || '';
            } else {
                this.phone_digits = digitsOnly(row[field]);
            }
            this.phone_error = '';
            this.phone_modal_visible = true;
        },
        closePhoneModal() {
            this.phone_modal_visible = false;
            this.phone_row = null;
            this.phone_field = '';
            this.phone_digits = '';
            this.phone_error = '';
        },
        onPhoneInput(e) {
            const raw = String((e && e.target && e.target.value) || this.phone_digits || '');
            if (this.cluster === 'asia') {
                this.phone_digits = raw.replace(/[^\d+\s]/g, '').slice(0, 16);
            } else {
                this.phone_digits = raw.replace(/\D/g, '').slice(0, 10);
            }
            this.phone_error = '';
        },
        async savePhone() {
            const formatted = this.cluster === 'asia'
                ? formatAsiaPhone(this.phone_digits)
                : formatUSPhone(this.phone_digits);
            if (!formatted) {
                this.phone_error = this.cluster === 'asia'
                    ? 'Enter a valid phone with country code (max 16 chars).'
                    : 'Enter exactly 10 digits.';
                return;
            }
            this.phone_saving = true;
            try {
                const payload = { sid: this.phone_row.sid, cluster: this.cluster };
                payload[this.phone_field] = formatted;
                const res = await this.updateVoiceaiContact(payload);
                if (!res || res.code !== 100000) {
                    message.error((res && res.msg) || 'Save phone failed');
                    return;
                }
                this.phone_row[this.phone_field] = formatted;
                message.success('Saved');
                this.closePhoneModal();
            } catch (e) {
                message.error((e && e.message) || 'Save phone failed');
            } finally {
                this.phone_saving = false;
            }
        },
        doDelete(row) {
            const that = this;
            Modal.confirm({
                title: 'Delete this store from Voice AI?',
                content: row.sname + ' / ' + row.sid,
                okType: 'danger',
                onOk: async () => {
                    const res = await that.deleteVoiceaiMerchant({ sid: row.sid, cluster: that.cluster });
                    if (res.code !== 100000) {
                        message.error(res.msg || 'Delete failed');
                        return;
                    }
                    message.success('Deleted');
                    that.fetchList();
                }
            });
        },
        async doSync(row) {
            this.$set(row, '_syncing', true);
            try {
                const res = await this.syncVoiceaiStoreInfo({ sid: row.sid, cluster: this.cluster });
                if (!res || res.code !== 100000) {
                    message.error((res && res.msg) || 'Sync failed');
                    return;
                }
                if (res.data) {
                    row.sname = res.data.sname;
                    row.timezone = res.data.timezone;
                }
                message.success('Store info synced');
                this.fetchList();
            } catch (e) {
                message.error((e && e.message) || 'Sync failed');
            } finally {
                this.$set(row, '_syncing', false);
            }
        },
        openDrawer(row) {
            this.edit_row = row;
            const parsed = parseOpenningConfig(row.openning_time_config);
            this.edit_hours = parsed.hours;
            this.edit_special = parsed.special;
            this.edit_callahead = row.callahead_enabled == 1;
            const promos = Array.isArray(row.promotions_config) ? row.promotions_config : [];
            this.edit_promos = promos.map(p => (typeof p === 'string' ? p : ((p && p.content) || ''))).filter(p => p !== undefined);

            this.edit_ai_hours_enable = row.ai_working_hours_enable == 1;
            const aiParsed = parseAiWorkingConfig(row.ai_working_hours_config);
            this.edit_ai_hours = aiParsed.hours;
            this.edit_ai_special = aiParsed.special;

            this.edit_online_order_enabled = row.online_order_enabled == 1;
            this.edit_online_order_url = row.online_order_url || '';
            this.edit_order_sms_template = (row.order_sms_template && String(row.order_sms_template).trim())
                ? row.order_sms_template
                : DEFAULT_ORDER_SMS_TEMPLATE;

            this.drawer_visible = true;
        },
        closeDrawer() {
            this.drawer_visible = false;
            this.edit_row = null;
        },
        addSpecial() {
            this.edit_special.push({
                date: '',
                label: '',
                mode: 'closed',
                from: '11:00 AM',
                to: '10:00 PM'
            });
        },
        removeSpecial(i) {
            this.edit_special.splice(i, 1);
        },
        onSpecialDateChange(i, dateStr) {
            this.edit_special[i].date = dateStr || '';
        },
        addAiSpecial() {
            this.edit_ai_special.push({
                date: '',
                label: '',
                mode: 'closed',
                from: '11:00 AM',
                to: '9:00 PM'
            });
        },
        removeAiSpecial(i) {
            this.edit_ai_special.splice(i, 1);
        },
        onAiSpecialDateChange(i, dateStr) {
            this.edit_ai_special[i].date = dateStr || '';
        },
        addPromo() {
            this.edit_promos.push('New promotion');
        },
        removePromo(i) {
            this.edit_promos.splice(i, 1);
        },
        async saveDrawer() {
            if (!this.edit_row) return;
            this.drawer_saving = true;
            try {
                const openning_time_config = buildOpenningConfig(this.edit_hours, this.edit_special);
                const ai_working_hours_config = buildAiWorkingConfig(this.edit_ai_hours, this.edit_ai_special);
                const promotions_config = this.edit_promos
                    .map(p => String(p || '').trim())
                    .filter(Boolean)
                    .map(content => ({ content }));

                const order_sms_template = String(this.edit_order_sms_template || '').trim() || DEFAULT_ORDER_SMS_TEMPLATE;
                const res = await this.updateVoiceaiSetting({
                    sid: this.edit_row.sid,
                    openning_time_config,
                    promotions_config,
                    callahead_enabled: this.edit_callahead ? 1 : 0,
                    ai_working_hours_enable: this.edit_ai_hours_enable ? 1 : 0,
                    ai_working_hours_config,
                    online_order_enabled: this.edit_online_order_enabled ? 1 : 0,
                    online_order_url: String(this.edit_online_order_url || '').trim(),
                    order_sms_template,
                    cluster: this.cluster
                });
                if (res.code !== 100000) {
                    message.error(res.msg || 'Save failed');
                    return;
                }
                message.success('Saved — live for the AI now');
                this.closeDrawer();
                this.fetchList();
            } catch (e) {
                message.error('Save failed');
            } finally {
                this.drawer_saving = false;
            }
        }
    }
};
</script>

<style scoped>
.voiceai-page {
    width: 100%;
    max-width: none;
    box-sizing: border-box;
    padding: 0 24px 40px;
}
.page-head {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 18px;
    width: 100%;
}
.page-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    margin-top: 4px;
    flex: 1;
}
.cluster-wrap {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-right: 8px;
}
.cluster-lbl {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-right: 4px;
}
.stat-wrap {
    margin-left: auto;
    display: flex;
    gap: 24px;
}
.stat-n {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
}
.stat-n.on {
    color: #52c41a;
}
.stat-l {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.add-card {
    margin-bottom: 16px;
}
.add-lbl {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 8px;
}
.add-row {
    display: flex;
    gap: 10px;
    width: 100%;
}
.add-row > .ant-input,
.add-row >>> .ant-input {
    flex: 1;
}
.add-hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.35);
    margin-top: 8px;
}
.store-name {
    font-weight: 600;
}
.store-sid {
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    color: #1890ff;
}
.voice-label {
    margin-left: 8px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.35);
}
.voice-label.on {
    color: #52c41a;
}
.phone-val {
    font-family: Consolas, Monaco, monospace;
    font-size: 12.5px;
}
.phone-edit {
    margin-left: 6px;
}
.action-btns {
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.action-btns .ant-btn + .ant-btn {
    margin-left: 12px;
}
.phone-input-row {
    display: flex;
    gap: 8px;
    align-items: stretch;
}
.phone-cc {
    display: flex;
    align-items: center;
    padding: 0 12px;
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    white-space: nowrap;
    font-weight: 600;
}
.phone-error {
    color: #f5222d;
    margin-top: 8px;
    font-size: 12px;
}
.foot-note {
    margin-top: 18px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.35);
    line-height: 1.7;
}
.drawer-meta {
    margin-bottom: 18px;
}
.drawer-store {
    font-size: 17px;
    font-weight: 700;
}
.drawer-sid {
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
}
.sect {
    margin-bottom: 22px;
}
.sect.nested {
    margin-top: 14px;
}
.sect-title {
    font-size: 14px;
    font-weight: 650;
    margin-bottom: 10px;
}
.sect-title.warn {
    color: #d48806;
    font-size: 13px;
}
.sect-sub {
    margin-left: 8px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.35);
    font-weight: 400;
}
.hour-row {
    display: grid;
    grid-template-columns: 50px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}
.hour-row.closed {
    opacity: 0.55;
}
.day {
    font-weight: 600;
}
.times {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.to-lbl {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}
.closed-lbl {
    color: #d48806;
    font-size: 11px;
    font-weight: 600;
}
.special-card {
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 8px;
    background: #fafafa;
}
.special-row1 {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
}
.special-row2 {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}
.special-times {
    display: flex;
    align-items: center;
    gap: 8px;
}
.open-lbl {
    color: #52c41a;
    font-weight: 600;
    font-size: 12px;
}
.preview-box {
    margin-top: 12px;
}
.field-lbl {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.85);
}
.svc-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 12px;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
}
.svc-name {
    font-weight: 600;
}
.svc-desc {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    margin-top: 3px;
}
.ai-hour-row {
    display: grid;
    grid-template-columns: 50px 1fr auto;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid #f0f0f0;
    padding: 10px 0;
}
.ai-hour-times {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
}
.empty-promo {
    color: rgba(0, 0, 0, 0.35);
    font-size: 12px;
    padding: 12px;
    text-align: center;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    margin-bottom: 8px;
}
.promo-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 8px;
}
.drawer-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    width: 100%;
    border-top: 1px solid #e9e9e9;
    padding: 10px 16px;
    background: #fff;
    text-align: right;
}
.voiceai-page >>> .ant-drawer-body {
    position: relative;
    padding-bottom: 96px;
}
</style>
